import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useMst } from '../../../models/rootModel';
import { PaywallMeta } from '../../../types/paywall';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { MainFunnelButtonV2 } from '../../common/v2/MainFunnelButtonV2';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { BaseStepPropsV3 } from '../../common/v3/BaseStepV3.types';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import {
  PickAProductContentV2Wrapper,
  PickAProductDesktopCtaSection,
  PickAProductDesktopWrapper,
  PickAProductInfo,
  PickAProductMobileCtaSection,
  PickAProductMobileCtaSectionContent,
  PickAProductStickyHeader,
  PickAProductStickyHeaderButtonWrapper,
  PickAProductStickyHeaderContent,
  PickAProductStickyInfo,
} from './PickAProductContentV2.style';
import { AdvantagesSection } from './sections/AdvantagesSection';
import { GuidesSection } from './sections/GuidesSection';
import { JourneySection } from './sections/JourneySection';
import { PickPlanSection } from './sections/PickAPlanSection';
import { StoriesSection } from './sections/StoriesSection';
import {Variant} from "../PickAProduct/PickAProduct.types";

interface PickAProductContentV2Props extends BaseStepPropsV3 {
  handleAbsoluteBack: () => void;
  handleSubmit: (position: string) => void;
  pickAProduct: (pp: string, id: string) => void;
  planPositions: string[];
  data: PaywallMeta;
  useAbsoluteHeaderNavigation?: boolean;
  variant: Variant;
}

export const PickAProductContentV2 = observer((props: PickAProductContentV2Props) => {
  const { data, planPositions, useAbsoluteHeaderNavigation, handleSubmit, pickAProduct, handleAbsoluteBack, isActive, variant } =
    props;

  const { UserPayment } = useMst();

  const [showStickyHeader, setShowStickyHeader] = useState(false);

  const { ref: submitButtonRef, inView: isSubmitButtonInView } = useInView();

  const handleStickyHeaderSubmit = () => {
    handleSubmit('scrolledHeader');
  };

  const handleMainSubmit = () => {
    handleSubmit('main');
  };

  useEffect(() => {
    if (!isActive) {
      setShowStickyHeader(false);
      return;
    }

    // to avoid flickering at the beginning of the step
    const timeout = setTimeout(() => {
      setShowStickyHeader(!isSubmitButtonInView);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [isActive, isSubmitButtonInView]);

  return (
    <BaseStepV3 {...props} addSpacingNeededForMobileButton={false}>
      <AbsoluteHeader onBack={useAbsoluteHeaderNavigation ? handleAbsoluteBack : undefined} />
      <PickAProductStickyHeader isVisible={showStickyHeader}>
        <PickAProductStickyHeaderContent>
          <PickAProductStickyInfo>{data.generalData.label}</PickAProductStickyInfo>
          <PickAProductStickyHeaderButtonWrapper>
            <MainFunnelButtonV2
              id="mainCta_pickPlan_sticky"
              text={data.generalData.button}
              onClick={handleStickyHeaderSubmit}
              disabled={false}
              isLoading={false}
              variant={MainButtonVariant.Purple}
            />
          </PickAProductStickyHeaderButtonWrapper>
        </PickAProductStickyHeaderContent>
      </PickAProductStickyHeader>

      <PickAProductContentV2Wrapper>
        <PickPlanSection
          chosenPlanPosition={UserPayment.chosenPlanPosition}
          stripePlans={UserPayment.stripePlans}
          planPositions={planPositions}
          data={data.generalData}
          pickAProduct={pickAProduct}
          variant={variant}
        />

        <PickAProductDesktopCtaSection>
          <PickAProductDesktopWrapper ref={submitButtonRef}>
            <MainFunnelButtonV2
              id="mainCta_pickPlan_main"
              text={data.generalData.button}
              onClick={handleMainSubmit}
              disabled={false}
              isLoading={false}
              variant={MainButtonVariant.Black}
            />
            <PickAProductInfo>{data.generalData.label}</PickAProductInfo>
          </PickAProductDesktopWrapper>
        </PickAProductDesktopCtaSection>

        <StoriesSection data={data.storiesSection} />
        <AdvantagesSection data={data.advantagesSection} />
        <JourneySection data={data.journeySection} />
        <GuidesSection data={data.guidesSection} />
      </PickAProductContentV2Wrapper>
      <PickAProductMobileCtaSection>
        <PickAProductMobileCtaSectionContent>
          <MainFunnelButtonV2
            id="mainCta_pickPlan_mobile"
            text={data.generalData.button}
            onClick={handleMainSubmit}
            disabled={false}
            isLoading={false}
            variant={MainButtonVariant.Black}
          />
          <PickAProductInfo>{data.generalData.label}</PickAProductInfo>
        </PickAProductMobileCtaSectionContent>
      </PickAProductMobileCtaSection>
    </BaseStepV3>
  );
});
